import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type AuthUser = {
  id: string;
  email: string;
  fullName: string;
  role: string;
};

type AuthState = {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  setToken: (token: string | null, user?: AuthUser | null) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

const STORAGE_KEY = 'nat_token';
const USER_KEY = 'nat_user';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [user, setUserState] = useState<AuthUser | null>(() => {
    try {
      const s = localStorage.getItem(USER_KEY);
      if (s) return JSON.parse(s);
      const t = localStorage.getItem(STORAGE_KEY);
      if (t) {
        try {
          const payload = JSON.parse(atob(t.split('.')[1] ?? '{}'));
          return {
            id: payload.sub ?? '',
            email: payload.email ?? '',
            fullName: payload.email ?? 'Utente',
            role: payload.role ?? 'OPERATOR',
          };
        } catch {
          return null;
        }
      }
      return null;
    } catch {
      return null;
    }
  });

  const setToken = useCallback((t: string | null, u?: AuthUser | null) => {
    setTokenState(t);
    setUserState((prev) => {
      const userToStore = u !== undefined ? u : (t ? prev : null);
      if (t) {
        localStorage.setItem(STORAGE_KEY, t);
        if (userToStore) localStorage.setItem(USER_KEY, JSON.stringify(userToStore));
      } else {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(USER_KEY);
      }
      return userToStore;
    });
  }, []);

  const logout = useCallback(() => setToken(null, null), [setToken]);

  useEffect(() => {
    const onUnauthorized = () => {
      setToken(null, null);
    };
    window.addEventListener('nat:unauthorized', onUnauthorized as EventListener);
    return () => window.removeEventListener('nat:unauthorized', onUnauthorized as EventListener);
  }, [setToken]);

  const value = useMemo<AuthState>(
    () => ({
      token,
      user,
      isAuthenticated: !!token,
      setToken,
      logout,
    }),
    [token, user, setToken, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}